// MeasurementInput — decimal input for m / m² / m³ assets (buildspec
// 20-ui §5.2, PRD §7.4.1).
//
// Why a button + bottom-sheet keypad (and NOT an <input>):
// on mobile, opening a native numeric keyboard occludes the capture row
// the user is trying to fill in. The row is precisely what they need to
// see while entering the measurement. We render a tappable field that
// opens `<NumericKeypadSheet>` — a custom bottom sheet — which keeps the
// row visible above the sheet AND lets us enforce "2 decimals max"
// on every key press instead of scrubbing user input after the fact.
//
// For m² and m³ rows we ALSO render a small adjacent calculator button
// that opens `<MeasurementCalculatorSheet>` — the surveyor enters
// length × width [× height] on a tape measure and the sheet computes
// the product, applying it back via the same `onChange` callback. The
// keypad path stays available so a surveyor with a pre-computed area
// can still type the value directly. Linear m has no product to
// compute, so it has no calculator button.
//
// Value contract:
//   - `value` is a canonical decimal string. "" means "not set".
//   - `onChange(next)` fires on every keypad interaction. The
//     calculator emits onChange only on Apply (intermediate dimension
//     edits are local to the sheet).
//   - Tapping Clear inside the keypad emits `onChange("")`.
//   - Validation: at most 2 decimal digits — enforced inside both
//     primitives so the callback is never invoked with a malformed
//     value.
//
// Touch / a11y contract:
//   - Tappable field ≥ 80 px wide so the value + unit render legibly
//     even for short values like "0 m".
//   - ≥ 44 px tall to satisfy the global touch-target invariant.
//   - Field rendered as a `<button>`, not a div, so screen readers
//     announce it as a control and keyboard users can reach it via Tab.
//   - Unit suffix rendered inline (e.g. "34.50 m²"); the symbol is
//     part of the visual label, not announced separately.

import { useState } from 'react'

import { Calculator } from 'lucide-react'

import { MeasurementCalculatorSheet } from '../../ui/measurement-calculator-sheet'
import { NumericKeypadSheet } from '../../ui/numeric-keypad-sheet'

export type MeasurementUnit = 'm' | 'm2' | 'm3'

export interface MeasurementInputProps {
  value: string
  onChange: (next: string) => void
  unit: MeasurementUnit
  disabled?: boolean
  ariaLabel: string
}

const UNIT_DISPLAY: Record<MeasurementUnit, string> = {
  m: 'm',
  m2: 'm²',
  m3: 'm³',
}

const FIELD_CLASSES =
  'inline-flex min-h-11 min-w-20 items-center justify-end gap-1 rounded-xl border border-[color:var(--color-cc-forest)]/20 bg-white px-3 py-2 text-base font-medium text-[color:var(--color-cc-forest)] tabular-nums transition hover:border-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)] disabled:cursor-not-allowed disabled:opacity-50'

const CALC_BUTTON_CLASSES =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-[color:var(--color-cc-forest)]/20 bg-white text-[color:var(--color-cc-forest)] transition hover:border-[color:var(--color-cc-forest)] hover:bg-[color:var(--color-cc-cream)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)] disabled:cursor-not-allowed disabled:opacity-50'

export function MeasurementInput({
  value,
  onChange,
  unit,
  disabled = false,
  ariaLabel,
}: MeasurementInputProps) {
  const [keypadOpen, setKeypadOpen] = useState(false)
  const [calcOpen, setCalcOpen] = useState(false)
  const unitDisplay = UNIT_DISPLAY[unit]
  const hasValue = value !== ''
  const displayValue = hasValue ? value : '—'
  const dimensionsForUnit: 2 | 3 | null =
    unit === 'm2' ? 2 : unit === 'm3' ? 3 : null

  return (
    <>
      <div className="inline-flex items-center gap-1">
        {dimensionsForUnit !== null ? (
          <button
            type="button"
            disabled={disabled}
            aria-label={`Calculate ${ariaLabel} from ${
              dimensionsForUnit === 2
                ? 'length and width'
                : 'length, width and height'
            }`}
            aria-haspopup="dialog"
            aria-expanded={calcOpen}
            onClick={() => setCalcOpen(true)}
            data-testid="measurement-input-calc-button"
            className={CALC_BUTTON_CLASSES}
          >
            <Calculator aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : null}
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={keypadOpen}
          onClick={() => setKeypadOpen(true)}
          data-testid="measurement-input-field"
          className={FIELD_CLASSES}
        >
          <span>
            {displayValue} {unitDisplay}
          </span>
        </button>
      </div>
      <NumericKeypadSheet
        open={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        value={value}
        onChange={onChange}
        decimal
        maxDecimals={2}
        unit={unitDisplay}
        ariaLabel={ariaLabel}
        title={ariaLabel}
      />
      {dimensionsForUnit !== null ? (
        <MeasurementCalculatorSheet
          open={calcOpen}
          onClose={() => setCalcOpen(false)}
          dimensions={dimensionsForUnit}
          unit={unitDisplay}
          currentValue={value}
          onApply={onChange}
          ariaLabel={ariaLabel}
          title={ariaLabel}
          maxDecimals={2}
        />
      ) : null}
    </>
  )
}
